<template>
  <article>
    <job-preview-content-outline
      title="雇用形態"
      :comment-highlights="getCommentHighlights(targetIdMap.requirements.employment)"
      :highlight-mode="highlightMode"
      :target-id="targetIdMap.requirements.employment"
      :value="requirements.employment.join(' ')"
      data-test="employment"
      v-on="$listeners"
    >
      <template
        v-if="requirements.employmentRemarks"
        #supplement
      >
        <job-preview-content
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.employmentRemarks)"
          :text-count-mode="textCountMode"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('試用期間の雇用形態備考', requirements.employmentRemarks, targetIdMap.requirements.employmentRemarks)]"
          :target-id="targetIdMap.requirements.employmentRemarks"
          :value="requirements.employmentRemarks"
          data-test="employmentRemarks"
          v-on="$listeners"
        />
      </template>
      <template
        v-if="requirements.regular"
        #sub
      >
        <job-preview-content-sub
          title="正社員登用について"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.regular)"
          :text-count-mode="textCountMode"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('正社員登用について', requirements.regular, targetIdMap.requirements.regular)]"
          :target-id="targetIdMap.requirements.regular"
          :value="requirements.regular"
          data-test="regular"
          v-on="$listeners"
        />
      </template>
    </job-preview-content-outline>

    <job-preview-content-outline
      title="給与"
      :comment-highlights="getCommentHighlights(targetIdMap.requirements.salary)"
      :text-count-mode="textCountMode"
      :highlight-mode="highlightMode"
      :text-counts="[getTextCount('給与', requirements.salary, targetIdMap.requirements.salary)]"
      :target-id="targetIdMap.requirements.salary"
      :value="requirements.salary"
      data-test="salary"
      v-on="$listeners"
    >
      <template
        v-if="requirements.salaryEx || (requirements.isBulkRegistrationAddress && isMultiLineValue)"
        #sub
      >
        <job-preview-content-sub
          v-if="requirements.salaryEx"
          title="給与例"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.salaryEx)"
          :text-count-mode="textCountMode"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('給与例', requirements.salaryEx, targetIdMap.requirements.salaryEx)]"
          :target-id="targetIdMap.requirements.salaryEx"
          :value="requirements.salaryEx"
          data-test="salaryEx"
          v-on="$listeners"
        />

        <job-preview-content-sub
          v-if="requirements.isBulkRegistrationAddress && isMultiLineValue"
          title="勤務地別給与"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.combinedAllSalaryBulkPlace)"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('勤務地別給与', requirements.combinedAllSalaryBulkPlace, targetIdMap.requirements.combinedAllSalaryBulkAddress)]"
          :target-id="targetIdMap.requirements.combinedAllSalaryBulkPlace"
          :value="requirements.combinedAllSalaryBulkPlace"
          data-test="combinedAllSalaryBulkPlace"
          v-on="$listeners"
        />
      </template>
    </job-preview-content-outline>

    <job-preview-content-outline
      title="勤務地"
      :comment-highlights="getCommentHighlights(targetIdMap.requirements.place)"
      :text-count-mode="textCountMode"
      :highlight-mode="highlightMode"
      :text-counts="[getTextCount('勤務地', requirements.place, targetIdMap.requirements.place)]"
      :target-id="targetIdMap.requirements.place"
      :value="requirements.place"
      data-test="place"
      v-on="$listeners"
    >
      <template #sub>
        <job-preview-content-sub
          v-if="requirements.transportation"
          title="詳細・交通"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.transportation)"
          :text-count-mode="textCountMode"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('詳細・交通', requirements.transportation, targetIdMap.requirements.transportation)]"
          :target-id="targetIdMap.requirements.transportation"
          :value="requirements.transportation"
          data-test="transportation"
          v-on="$listeners"
        />
        <job-preview-content-sub
          v-if="requirements.transfer"
          title="転勤の可能性"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.transfer)"
          :text-count-mode="textCountMode"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('転勤の可能性', requirements.transfer, targetIdMap.requirements.transfer)]"
          :target-id="targetIdMap.requirements.transfer"
          :value="requirements.transfer"
          data-test="transfer"
          v-on="$listeners"
        />

        <job-preview-content-sub-work-location
          v-if="requirements.isBulkRegistrationAddress && requirements.combinedAllBulkPlace"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.combinedAllBulkPlace)"
          :highlight-mode="highlightMode"
          :value="requirements.combinedAllBulkPlace"
          title="勤務地一覧"
          :is-bulk-registration-address="requirements.isBulkRegistrationAddress"
          :place-id="targetIdMap.requirements.combinedAllBulkPlace"
          data-test="combinedAllBulkPlace"
          v-on="$listeners"
        />

        <job-preview-content-sub-work-location
          v-if="!requirements.isBulkRegistrationAddress"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.workLocation)"
          :highlight-mode="highlightMode"
          :value="requirements.workLocation"
          title="勤務地エリア"
          :place-id="targetIdMap.requirements.workLocation"
          data-test="workLocation"
          v-on="$listeners"
        />
      </template>
    </job-preview-content-outline>

    <job-preview-content-outline
      title="応募資格"
      :comment-highlights="getCommentHighlights(targetIdMap.requirements.requiredSkill)"
      :text-count-mode="textCountMode"
      :highlight-mode="highlightMode"
      :text-counts="[getTextCount('応募資格', requirements.requiredSkill, targetIdMap.requirements.requiredSkill)]"
      :target-id="targetIdMap.requirements.requiredSkill"
      :value="requirements.requiredSkill"
      data-test="requiredSkill"
      v-on="$listeners"
    >
      <template #sub>
        <job-preview-content-sub
          v-if="requirements.welcomeSkill"
          title="あると望ましい経験・能力"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.welcomeSkill)"
          :text-count-mode="textCountMode"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('あると望ましい経験・能力', requirements.welcomeSkill, targetIdMap.requirements.welcomeSkill)]"
          :target-id="targetIdMap.requirements.welcomeSkill"
          :value="requirements.welcomeSkill"
          data-test="welcomeSkill"
          v-on="$listeners"
        />
        <job-preview-content-sub
          v-if="requirements.recruitExample"
          title="過去に採用した転職者例"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.recruitExample)"
          :text-count-mode="textCountMode"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('過去に採用した転職者例', requirements.recruitExample, targetIdMap.requirements.recruitExample)]"
          :target-id="targetIdMap.requirements.recruitExample"
          :value="requirements.recruitExample"
          data-test="recruitExample"
          v-on="$listeners"
        />
      </template>
    </job-preview-content-outline>

    <job-preview-content-outline
      v-if="isShow.post"
      title="配属部署"
      :comment-highlights="getCommentHighlights(targetIdMap.requirements.post)"
      :text-count-mode="textCountMode"
      :highlight-mode="highlightMode"
      :text-counts="[getTextCount('配属部署', requirements.post, targetIdMap.requirements.post)]"
      :target-id="targetIdMap.requirements.post"
      :value="requirements.post"
      data-test="post"
      v-on="$listeners"
    >
      <template #sub>
        <job-preview-content-sub
          v-if="requirements.ageRatio"
          title="年齢構成"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.ageRatio)"
          :text-count-mode="textCountMode"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('年齢構成', requirements.ageRatio, targetIdMap.requirements.ageRatio)]"
          :target-id="targetIdMap.requirements.ageRatio"
          :value="requirements.ageRatio"
          data-test="ageRatio"
          v-on="$listeners"
        />
        <job-preview-content-sub
          v-if="requirements.sexRatio"
          title="男女比"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.sexRatio)"
          :text-count-mode="textCountMode"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('男女比', requirements.sexRatio, targetIdMap.requirements.sexRatio)]"
          :target-id="targetIdMap.requirements.sexRatio"
          :value="requirements.sexRatio"
          data-test="sexRatio"
          v-on="$listeners"
        />
      </template>
    </job-preview-content-outline>

    <job-preview-content-outline
      title="勤務時間"
      :comment-highlights="getCommentHighlights(targetIdMap.requirements.officeHours)"
      :text-count-mode="textCountMode"
      :highlight-mode="highlightMode"
      :text-counts="[getTextCount('勤務時間', requirements.officeHours, targetIdMap.requirements.officeHours)]"
      :target-id="targetIdMap.requirements.officeHours"
      :value="requirements.officeHours"
      data-test="officeHours"
      v-on="$listeners"
    >
      <template #sub>
        <job-preview-content-sub
          v-if="requirements.overTime"
          title="残業について"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.overTime)"
          :text-count-mode="textCountMode"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('残業について', requirements.overTime, targetIdMap.requirements.overTime)]"
          :target-id="targetIdMap.requirements.overTime"
          :value="requirements.overTime"
          data-test="overTime"
          v-on="$listeners"
        />
      </template>
    </job-preview-content-outline>

    <job-preview-content-outline
      v-if="requirements.mother"
      title="育児と両立しやすい"
      :comment-highlights="getCommentHighlights(targetIdMap.requirements.mother)"
      :text-count-mode="textCountMode"
      :highlight-mode="highlightMode"
      :text-counts="[getTextCount('育児と両立しやすい', requirements.mother, targetIdMap.requirements.mother)]"
      :target-id="targetIdMap.requirements.mother"
      :value="requirements.mother"
      data-test="mother"
      v-on="$listeners"
    />

    <job-preview-content-outline
      title="休日休暇"
      :comment-highlights="getCommentHighlights(targetIdMap.requirements.vacation)"
      :text-count-mode="textCountMode"
      :highlight-mode="highlightMode"
      :text-counts="[getTextCount('休日休暇', requirements.vacation, targetIdMap.requirements.vacation)]"
      :target-id="targetIdMap.requirements.vacation"
      :value="requirements.vacation"
      data-test="vacation"
      v-on="$listeners"
    >
      <template
        v-if="requirements.maternity"
        #sub
      >
        <job-preview-content-sub
          title="産休育休活用例"
          :comment-highlights="getCommentHighlights(targetIdMap.requirements.maternity)"
          :text-count-mode="textCountMode"
          :highlight-mode="highlightMode"
          :text-counts="[getTextCount('産休育休活用例', requirements.maternity, targetIdMap.requirements.maternity)]"
          :target-id="targetIdMap.requirements.maternity"
          :value="requirements.maternity"
          data-test="maternity"
          v-on="$listeners"
        />
      </template>
    </job-preview-content-outline>

    <job-preview-content-outline
      title="待遇・福利厚生・その他"
      :comment-highlights="getCommentHighlights(targetIdMap.requirements.condition)"
      :text-count-mode="textCountMode"
      :highlight-mode="highlightMode"
      :text-counts="[getTextCount('待遇・福利厚生・その他', requirements.condition, targetIdMap.requirements.condition)]"
      :target-id="targetIdMap.requirements.condition"
      :value="requirements.condition"
      data-test="condition"
      v-on="$listeners"
    />
  </article>
</template>

<style scoped lang="scss">
@use "@/styles/preview/pc/offer/base" as *;
</style>

<script>
import JobPreviewContentOutline from '@/components/atoms/JobPreviewContentOutline'
import JobPreviewContentSub from '@/components/atoms/JobPreviewContentSub'
import JobPreviewContentSubWorkLocation from '@/components/atoms/JobPreviewContentSubWorkLocation'
import jobPreviewCommon from '@/lib/jobPreviewCommon'
import jobPreviewHighlight from '@/lib/jobPreviewHighlight'
import JobPreviewContent from '@/components/atoms/JobPreviewContent'

export default {
  components: {
    JobPreviewContentOutline,
    JobPreviewContentSub,
    JobPreviewContentSubWorkLocation,
    JobPreviewContent
  },
  mixins: [jobPreviewCommon, jobPreviewHighlight],
  props: {
    requirements: {
      type: Object,
      required: true
    },
    commentHighlights: {
      type: Array,
      default: () => []
    },
    highlightMode: {
      type: Boolean,
      required: true
    },
    textCountMode: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    isMultiLineValue () {
      if (this.requirements.combinedAllSalaryBulkPlace) {
        return this.requirements.combinedAllSalaryBulkPlace.split('\n').length > 1
      }
      return false
    }
  }
}
</script>
